import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BlockIcon from "@mui/icons-material/Block";
import RestoreIcon from "@mui/icons-material/Restore";
import {
  listModules,
  createModule,
  updateModule,
  deactivateModule,
  reactivateModule,
} from "../../../../utils/services/control/rbacAdminService";
import StatusChip from "../common/StatusChip";
import ConfirmDialog from "../common/ConfirmDialog";
import ExportExcelButton from "../../../../components/Basic/ExportExcelButton";
import CatalogFormDialog from "./CatalogFormDialog";
import useSnack from "../../hooks/useSnack";

const MODULE_EXPORT_COLUMNS = [
  { field: "moduleId", headerName: "Module ID" },
  { field: "moduleName", headerName: "Module Name" },
  { field: "moduleKey", headerName: "Module Key" },
  { field: "description", headerName: "Description" },
  { field: "rolePermissionsCount", headerName: "Role Permissions Count" },
  {
    field: "status",
    headerName: "Status",
    valueGetter: (r) => (r.status === 1 ? "Active" : "Inactive"),
  },
];

const FORM_LABELS = {
  entityName: "Module",
  nameLabel: "Module Name",
  keyLabel: "Module Key",
  keyHelper: "Stable key — auto-normalized. Cannot be changed later.",
  errName: "Module name is required",
  errKey: "Module key is required",
};

const ModulesCatalogTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [editEntity, setEditEntity] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [saving, setSaving] = useState(false);

  const { snack, showSnack, closeSnack } = useSnack();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listModules(statusFilter === "" ? null : statusFilter);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading modules:", err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async ({ name, key, description }) => {
    setSaving(true);
    try {
      const res = editEntity
        ? await updateModule({
            moduleId: editEntity.moduleId,
            moduleName: name,
            description,
          })
        : await createModule({
            moduleName: name,
            moduleKey: key,
            description,
          });
      if (res.result !== 0) {
        showSnack(res.message, "error");
      } else {
        showSnack(
          editEntity
            ? "Module updated successfully"
            : "Module created successfully"
        );
        setFormOpen(false);
        setEditEntity(null);
        loadData();
      }
    } catch (err) {
      showSnack(err.response?.data?.message || "Error saving module", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    const m = confirmAction.module;
    setSaving(true);
    try {
      const res = await deactivateModule(m.moduleId);
      if (res.result !== 0) {
        showSnack(res.message, "error");
      } else {
        showSnack(
          `Module deactivated successfully — ${res.permissionsRemoved ?? 0} role-permission(s) were removed.`
        );
        loadData();
      }
    } catch (err) {
      showSnack(err.response?.data?.message || "Error", "error");
    } finally {
      setSaving(false);
      setConfirmAction(null);
    }
  };

  const handleReactivate = async () => {
    const m = confirmAction.module;
    setSaving(true);
    try {
      const res = await reactivateModule(m.moduleId);
      if (res.result !== 0) {
        showSnack(res.message, "error");
      } else {
        showSnack("Module reactivated successfully");
        loadData();
      }
    } catch (err) {
      showSnack(err.response?.data?.message || "Error", "error");
    } finally {
      setSaving(false);
      setConfirmAction(null);
    }
  };

  const columns = [
    {
      field: "moduleName",
      headerName: "Module Name",
      flex: 1.2,
      minWidth: 180,
    },
    {
      field: "moduleKey",
      headerName: "Module Key",
      width: 180,
      renderCell: ({ value }) => (
        <Typography variant="caption" fontFamily="monospace">
          {value}
        </Typography>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.5,
      minWidth: 200,
      renderCell: ({ value }) => (
        <Typography variant="body2" noWrap title={value}>
          {value || "—"}
        </Typography>
      ),
    },
    {
      field: "rolePermissionsCount",
      headerName: "Role Permissions",
      width: 140,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Chip label={value ?? 0} size="small" variant="outlined" />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: ({ value }) => <StatusChip active={value === 1} />,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => {
                setEditEntity({
                  moduleId: row.moduleId,
                  name: row.moduleName,
                  key: row.moduleKey,
                  description: row.description,
                });
                setFormOpen(true);
              }}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.status === 1 ? (
            <Tooltip title="Deactivate">
              <IconButton
                size="small"
                color="error"
                onClick={() =>
                  setConfirmAction({ type: "deactivate", module: row })
                }
              >
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Reactivate">
              <IconButton
                size="small"
                color="success"
                onClick={() =>
                  setConfirmAction({ type: "reactivate", module: row })
                }
              >
                <RestoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <ExportExcelButton
          rows={rows}
          columns={MODULE_EXPORT_COLUMNS}
          filename="modules"
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditEntity(null);
            setFormOpen(true);
          }}
        >
          New Module
        </Button>
      </Box>

      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.moduleId}
        loading={loading}
        autoHeight
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 50 } },
        }}
        disableRowSelectionOnClick
        density="comfortable"
        sx={{ border: "none" }}
      />

      <CatalogFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditEntity(null);
        }}
        onSave={handleSave}
        entity={editEntity}
        labels={FORM_LABELS}
        loading={saving}
      />

      <ConfirmDialog
        open={confirmAction?.type === "deactivate"}
        title="Deactivate Module"
        message={`Deactivate module "${confirmAction?.module?.moduleName}"? All role-permissions for this module will be removed. After deactivation, no role will have access to this module.`}
        confirmLabel="Deactivate"
        onConfirm={handleDeactivate}
        onCancel={() => setConfirmAction(null)}
        loading={saving}
      />

      <ConfirmDialog
        open={confirmAction?.type === "reactivate"}
        title="Reactivate Module"
        message={`Reactivate module "${confirmAction?.module?.moduleName}"? Role-permissions will NOT be restored — you must re-grant them manually.`}
        confirmLabel="Reactivate"
        confirmColor="success"
        onConfirm={handleReactivate}
        onCancel={() => setConfirmAction(null)}
        loading={saving}
      />

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={closeSnack}>
        <Alert severity={snack.severity} onClose={closeSnack}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ModulesCatalogTab;
